import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Divider, List, ListItem, ListItemText, ListItemIcon,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Grid, Card, CardContent, CardActionArea, Chip, Button
} from '@mui/material';
import StorageIcon from '@mui/icons-material/Storage';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import HowToVoteIcon from '@mui/icons-material/HowToVote';
import ArticleIcon from '@mui/icons-material/Article';
import MapIcon from '@mui/icons-material/Map';
import BalanceIcon from '@mui/icons-material/Balance';
import CreateIcon from '@mui/icons-material/Create';
import ApiIcon from '@mui/icons-material/Api';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import SearchIcon from '@mui/icons-material/Search';

export default function Help() {
  const navigate = useNavigate();

  const dataCategories = [
    {
      title: "People & Structure",
      icon: <PeopleIcon />,
      color: "#1976d2",
      items: [
        { name: "Parliamentarians", path: "/parliamentarians", desc: "1,877 MPs and Senators since 1901" },
        { name: "Electorates", path: "/electorates", desc: "219 federal electoral divisions with maps" },
        { name: "Parties", path: "/parties", desc: "57 political parties" },
        { name: "Committees", path: "/committees", desc: "465 parliamentary committees" },
        { name: "Ministries", path: "/ministries", desc: "Government and shadow ministries" },
      ]
    },
    {
      title: "Chamber Documents",
      icon: <DescriptionIcon />,
      color: "#388e3c",
      items: [
        { name: "Bills", path: "/bills", desc: "2,240 pieces of legislation" },
        { name: "Notice Papers", path: "/house-notice-papers", desc: "House & Senate notice papers" },
        { name: "Standing Orders", path: "/standingorders", desc: "House & Senate procedural rules" },
        { name: "Calendar", path: "/calendar", desc: "Sitting calendar and events" },
      ]
    },
    {
      title: "Chamber Records",
      icon: <RecordVoiceOverIcon />,
      color: "#f57c00",
      items: [
        { name: "Hansard", path: "/hansard", desc: "507,000+ speeches from 1901-present" },
        { name: "Divisions", path: "/divisions", desc: "House and Senate voting records" },
        { name: "Votes & Proceedings", path: "/votes-proceedings", desc: "Official House records" },
        { name: "Senate Journals", path: "/senate-journals", desc: "Official Senate records" },
        { name: "Tabled Documents", path: "/tableddocuments", desc: "34,000+ tabled papers" },
      ]
    },
    {
      title: "Questions",
      icon: <QuestionAnswerIcon />,
      color: "#7b1fa2",
      items: [
        { name: "Senate Estimates QoN", path: "/questions", desc: "Senate Estimates questions" },
        { name: "Senate QoN", path: "/senateqon", desc: "Senate Questions on Notice" },
        { name: "House QiW", path: "/houseqiw", desc: "House Questions in Writing" },
      ]
    },
    {
      title: "Committee Reports",
      icon: <ArticleIcon />,
      color: "#c62828",
      items: [
        { name: "Committee Reports", path: "/committee-reports", desc: "63,000+ committee reports with full text" },
      ]
    },
    {
      title: "Petitions",
      icon: <CreateIcon />,
      color: "#00838f",
      items: [
        { name: "Petitions", path: "/petitions", desc: "5,000+ e-petitions and paper petitions" },
      ]
    },
    {
      title: "Reference",
      icon: <BalanceIcon />,
      color: "#5d4037",
      items: [
        { name: "Constitution", path: "/constitution", desc: "Full text with section search" },
        { name: "Australia Act", path: "/australiaact", desc: "Australia Act 1986" },
      ]
    },
  ];

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 1 }}>
        Help & Guide
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        ParlSearch provides unified access to over 700,000 records of Australian Parliamentary data.
      </Typography>

      {/* Quick Links to Data Sources */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: '#f8f9fa' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            What's in ParlSearch?
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Chip
              icon={<SearchIcon />}
              label="Try Guided Search"
              color="primary"
              clickable
              onClick={() => navigate('/guided-search')}
            />
            <Chip
              icon={<StorageIcon />}
              label="View Full Data Inventory"
              color="primary"
              variant="outlined"
              clickable
              onClick={() => navigate('/data-sources')}
            />
            <Chip
              icon={<NewReleasesIcon />}
              label="View Changelog"
              variant="outlined"
              clickable
              onClick={() => navigate('/changelog')}
            />
          </Box>
        </Box>

        <Grid container spacing={2}>
          {dataCategories.map((category) => (
            <Grid item xs={12} sm={6} md={4} key={category.title}>
              <Card variant="outlined" sx={{ height: '100%' }}>
                <CardContent sx={{ pb: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Box sx={{ color: category.color }}>{category.icon}</Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                      {category.title}
                    </Typography>
                  </Box>
                  <List dense sx={{ py: 0 }}>
                    {category.items.map((item) => (
                      <ListItem
                        key={item.name}
                        sx={{ py: 0.25, px: 0, cursor: 'pointer' }}
                        onClick={() => navigate(item.path)}
                      >
                        <ListItemText
                          primary={item.name}
                          secondary={item.desc}
                          primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Have a suggestion? */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 3, bgcolor: '#f8f9fa' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
              Have a suggestion?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We're always looking to improve ParlSearch. If you have an idea
              for a new feature or improvement, we'd love to hear from you.
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={() => navigate('/feature-request')}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Submit Feature Request
          </Button>
        </Box>
      </Paper>

      {/* Search Operators Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Search Syntax
        </Typography>
        <Typography variant="body1" sx={{ mb: 2 }}>
          ParlSearch uses Lucene query syntax across all data types for powerful and flexible searching.
        </Typography>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Operator</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Example</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell><code>field:value</code></TableCell>
                <TableCell>Search in a specific field</TableCell>
                <TableCell><code>title:"climate change"</code></TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>AND</code></TableCell>
                <TableCell>Both terms must be present</TableCell>
                <TableCell><code>climate AND agriculture</code></TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>OR</code></TableCell>
                <TableCell>Either term can be present</TableCell>
                <TableCell><code>Senate OR House</code></TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>NOT</code></TableCell>
                <TableCell>Exclude results with this term</TableCell>
                <TableCell><code>health NOT mental</code></TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>"phrase"</code></TableCell>
                <TableCell>Search for exact phrase</TableCell>
                <TableCell><code>"mental health services"</code></TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>(grouping)</code></TableCell>
                <TableCell>Group terms together</TableCell>
                <TableCell><code>(climate OR environment) AND policy</code></TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>*</code></TableCell>
                <TableCell>Wildcard (matches any characters)</TableCell>
                <TableCell><code>educat*</code> matches education, educational, etc.</TableCell>
              </TableRow>
              <TableRow>
                <TableCell><code>[range TO range]</code></TableCell>
                <TableCell>Date or numeric range</TableCell>
                <TableCell><code>year:[2020 TO 2024]</code></TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Common Fields by Data Type */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Common Search Fields
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Different data types have different searchable fields. Here are some common ones:
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Hansard</Typography>
            <List dense sx={{ py: 0 }}>
              <ListItem sx={{ py: 0 }}><code>speaker</code> - Name of the speaker</ListItem>
              <ListItem sx={{ py: 0 }}><code>chamber</code> - Senate or House</ListItem>
              <ListItem sx={{ py: 0 }}><code>date</code> - Date of speech</ListItem>
              <ListItem sx={{ py: 0 }}><code>content</code> - Full speech text</ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Bills</Typography>
            <List dense sx={{ py: 0 }}>
              <ListItem sx={{ py: 0 }}><code>title</code> - Bill title</ListItem>
              <ListItem sx={{ py: 0 }}><code>status</code> - Current status</ListItem>
              <ListItem sx={{ py: 0 }}><code>portfolio</code> - Responsible portfolio</ListItem>
              <ListItem sx={{ py: 0 }}><code>parliament_number</code> - Parliament session</ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Parliamentarians</Typography>
            <List dense sx={{ py: 0 }}>
              <ListItem sx={{ py: 0 }}><code>name</code> - Full name</ListItem>
              <ListItem sx={{ py: 0 }}><code>party</code> - Political party</ListItem>
              <ListItem sx={{ py: 0 }}><code>electorate</code> - Electorate represented</ListItem>
              <ListItem sx={{ py: 0 }}><code>chamber</code> - Senate or House</ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>Petitions</Typography>
            <List dense sx={{ py: 0 }}>
              <ListItem sx={{ py: 0 }}><code>title</code> - Petition title</ListItem>
              <ListItem sx={{ py: 0 }}><code>reason</code> - Petition reason</ListItem>
              <ListItem sx={{ py: 0 }}><code>request</code> - What is requested</ListItem>
              <ListItem sx={{ py: 0 }}><code>status</code> - Petition status</ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>

      {/* Search Examples */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Example Searches
        </Typography>

        <List>
          <ListItem>
            <ListItemText
              primary={<code>speaker:"Anthony Albanese" AND climate</code>}
              secondary="Find Hansard speeches by Anthony Albanese mentioning climate"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary={<code>title:"health" AND status:Passed</code>}
              secondary="Find passed bills with 'health' in the title"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary={<code>party:Labor AND chamber:Senate</code>}
              secondary="Find Labor senators"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary={<code>type:e-petition AND signature_count:[1000 TO *]</code>}
              secondary="Find e-petitions with 1000+ signatures"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary={<code>committee_type:Joint AND year:2024</code>}
              secondary="Find Joint Committee reports from 2024"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary={<code>"First Nations" OR "Indigenous"</code>}
              secondary="Find content mentioning First Nations or Indigenous"
            />
          </ListItem>
        </List>
      </Paper>

      {/* Tips Section */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          Tips
        </Typography>

        <List>
          <ListItem>
            <ListItemText
              primary="Use facet filters"
              secondary="Most search pages have facet filters on the left side - combine these with text search for precise results"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="Operators are case-sensitive"
              secondary="Use uppercase AND, OR, NOT (lowercase versions will be treated as search terms)"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="Share your searches"
              secondary="The URL updates with your search query - copy and share to show others your exact results"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="Explore the interactive map"
              secondary="The Electorates Map shows electoral boundaries from 1903 to present with a timeline slider"
            />
          </ListItem>
          <Divider />
          <ListItem>
            <ListItemText
              primary="Historic Hansard goes back to 1901"
              secondary="Search speeches from Federation onwards - nearly 500,000 historic speeches are indexed"
            />
          </ListItem>
        </List>
      </Paper>

      {/* API Access */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          <ApiIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
          API Access
        </Typography>
        <Typography variant="body1" paragraph>
          ParlSearch provides a REST API for programmatic access to all data. The API supports
          the same Lucene query syntax as the web interface.
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Base URL:</strong> <code>https://api.parlsearch.cc</code>
        </Typography>
        <Typography variant="body2" paragraph>
          Example endpoints:
        </Typography>
        <List dense>
          <ListItem><code>GET /api/hansard/search?q=climate</code></ListItem>
          <ListItem><code>GET /api/parliamentarians/search?q=party:Labor</code></ListItem>
          <ListItem><code>GET /api/bills/search?q=status:Passed</code></ListItem>
          <ListItem><code>GET /api/petitions/search?q=type:e-petition</code></ListItem>
        </List>
        <Typography variant="body2" sx={{ mt: 2 }}>
          Full API documentation:{' '}
          <a href="https://api.parlsearch.cc/api/docs" target="_blank" rel="noopener noreferrer">
            https://api.parlsearch.cc/api/docs
          </a>
        </Typography>
      </Paper>

      {/* About Section */}
      <Paper elevation={1} sx={{ p: 3, borderRadius: 3 }}>
        <Typography variant="h5" sx={{ mb: 2, fontWeight: 600 }}>
          About ParlSearch
        </Typography>
        <Typography variant="body1" paragraph>
          ParlSearch provides open access to Australian Parliamentary data, aggregating information
          from the Parliament of Australia, Australian Electoral Commission, and open source projects.
        </Typography>
        <Typography variant="body1" paragraph>
          The goal is to make parliamentary information more accessible and searchable than
          the official sources, with unified search across all data types and time periods.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View the complete{' '}
          <span
            onClick={() => navigate('/data-sources')}
            style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
          >
            data sources inventory
          </span>{' '}
          to see where all the data comes from, or check the{' '}
          <span
            onClick={() => navigate('/changelog')}
            style={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline' }}
          >
            changelog
          </span>{' '}
          to see what's new.
        </Typography>
      </Paper>
    </Box>
  );
}
